import { useCallback, useEffect, useRef } from 'react';

/**
 * Whether the focused element got its focus from the keyboard (Tab, or a dialog handing
 * focus back after a key) rather than from a mouse click. Read when focus arrives: by the
 * time a later keydown reaches a listener, the browser already shows the focus ring for
 * that key, so `:focus-visible` then says "keyboard" for a button that was clicked.
 *
 * @param {boolean} enabled - listen only while the page needs it
 * @returns {(element: Element) => boolean} true when `element` was focused by keyboard;
 *   true too for an element focused before listening began, which keeps the old rule
 */
export const useFocusedByKeyboard = enabled => {
	const focus = useRef({ element: null, byKeyboard: true });

	useEffect(() => {
		if (!enabled) return undefined;
		const onFocusIn = event => {
			focus.current = {
				element: event.target,
				byKeyboard: event.target.matches?.(':focus-visible') ?? true,
			};
		};
		document.addEventListener('focusin', onFocusIn, true);
		return () => document.removeEventListener('focusin', onFocusIn, true);
	}, [enabled]);

	return useCallback(
		element => focus.current.element !== element || focus.current.byKeyboard,
		[]
	);
};
